import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {Configuration} from '@shared/models/configuration';
import {GraphqlContentScope} from '@shared/models/graphql-content-scope.model';
import {TaskManagerService} from '../../services/task-manager.service';
import {select, Store} from '@ngrx/store';
import {IVirtualGridCollectionState} from '@shared/store/reducers/virtual-grid.reducer';
import {getVirtualGridItemsMetaData} from '@shared/store/selectors/virtual-grid.selector';
import {take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {PlatformCommand} from '@shared/commands/command-base';
import {DialogService} from '@shared/services/dialog-service';
import {CommandService} from '@core/services/command.service';
import {getStatusList, getTaskViewUpdateInfo} from '@features/task-manager/store/task-manager.selector';
import {TaskManagerState} from '@features/task-manager/store/task-manager.reducer';
import {GraphQLService} from '@core/services/graphql.service';
import {TaskManagerConfig} from '@features/task-manager/helpers/task-manager.config';
import {TaskManagerQuery} from '@features/task-manager/helpers/task-manager.query';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {Guid} from 'guid-typescript';
import {TicketOwner, TicketReviewer} from '@features/task-manager/constants/task-manager.constants';
import {UpdateStatusCommand} from '@features/task-manager/commands/task-manager.command';

@Component({
    standalone: false,
    selector: 'particle-task-sub-tickets',
  templateUrl: './task-sub-tickets.component.html',
  styleUrls: ['./task-sub-tickets.component.scss']
})
export class TaskSubTicketsComponent implements OnInit, OnDestroy {
  private id = 'task-sub-tickets';
  dynamicData: any;
  data: any;
  card: any;
  parentCard: any;
  board: any;
  addSubTask = false;
  sourceKey: string;
  invalidateSubject: InvalidateSubject;
  hasOccurrencePermission: boolean;
  private ngUnsubscribe$ = new Subject<void>();
  hasPermissionForChangeStatus = true;
  statusList = [];
  selectedStatus = '';
  subTasks = [];
  currentlyExpandedSubtaskId = '';
  @Output() actionEvent$: EventEmitter<any> = new EventEmitter();
  pageNo = 0;
  pageSize = 10;
  pageCount = 1;
  statusHidden = false;
  taskMenuItemsMapper:  any = {};
  api = TaskManagerConfig.TaskManagerApiKey;
  constructor(private taskManagerService: TaskManagerService, private dialogService: DialogService,
              private taskManagerStore: Store<TaskManagerState>,
              private commandService: CommandService,
              public changeDetectorRef: ChangeDetectorRef,
              private snackBarService: SnackBarService,
              private graphQLService: GraphQLService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private virtualGridStore: Store<IVirtualGridCollectionState>) {
    this.sourceKey = 'SubTasks';
    this.invalidateSubject = new InvalidateSubject();
  }

  ngOnInit() {
    if(this.dynamicData.parameters?.taskApi) {
      this.api = this.dynamicData.parameters.taskApi;
      this.taskManagerService.taskApi = this.api;
    }
    if (this.dynamicData && this.dynamicData.board) {
      this.board = this.dynamicData.board;
    }
    if (this.dynamicData && this.dynamicData.card) {
      this.card = this.dynamicData.card;
      this.setPermission();
      let parentTicketId = this.card.id;
      const isSubTask = !!this.card.parentTicket;
      if (!isSubTask) { // not a subtask, then assign this as parent task
        this.parentCard = this.card;
        this.setCardPermission(this.parentCard);
      } else {
        this.currentlyExpandedSubtaskId = this.card.id;
        parentTicketId = this.card.parentTicket.id;
        this.getParentTicket(parentTicketId, this.card.scope);
      }
      this.getSubtasks(parentTicketId, this.card.scope, this.pageNo, this.pageSize, isSubTask ? this.card.id : '');
    }
    this.setStatusDropdownItems();
    this.subscribeToInvalidatorListener();
  }

  getSubtasks(ticketId, scope, pageNo, pageSize, targetSubTaskId = '') {
    // target subtaskId is used to paginate to exact position of current subtask into subtaskList
    if (ticketId) {
      this.fuseSplashScreenService.show();
      this.graphQLService.executePostRequest(this.api, TaskManagerQuery.getSubTasks(ticketId, scope, pageNo, pageSize, targetSubTaskId)).pipe(take(1))
        .subscribe(payload => {
          this.fuseSplashScreenService.hide();
          if (payload && payload.data && payload.data.subTickets) {
            this.subTasks = payload.data.subTickets.items;
            this.pageCount = payload.data.subTickets.pageCount;
            this.pageNo = payload.data.subTickets.pageNo;
            this.pageSize = payload.data.subTickets.pageSize;
            this.setSubTasksPermission();
            setTimeout(() => {
              this.changeDetectorRef.detectChanges();
              this.navigateToSelectiveSubTaskView('task-sub-task-view');
            }, 100);
          } else {
            this.snackBarService.showError('Sub task fetching failed!');
          }
        });
    }
  }

  setStatusDropdownItems() {
    this.taskManagerStore.select(getStatusList).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(currentStatusList => {
      this.setSelectedStatusList(currentStatusList);
    });
  }

  private setSelectedStatusList(currentStatusList: any[]) {
    this.statusList = [];
    let cnt = 0;
    currentStatusList.forEach(element => {
      element.id = cnt++;
      this.statusList.push(element);
    });
  }

  onGridCellValueChange($event) {
    if ($event.action) {
      switch ($event.action.id) {
        case 'Open':
          try {
            this.navigate($event.item.cells.Title.value);
          } catch (e) {
            console.log(e);
          }
          break;
        case 'Delete':
          try {
            this.taskManagerService.deleteTask($event.item.cells.Title.value, this.invalidateSubject);
          } catch (e) {
            console.log(e);
          }
          break;
        case 'Edit':
          this.openEditor($event);
          break;
        case 'Disable':
          this.taskManagerService.toggleTicketState($event.item.cells.Title.value, true,
            this.invalidateSubject, false);
          break;
        case 'Enable':
          this.taskManagerService.toggleTicketState($event.item.cells.Title.value, false,
            this.invalidateSubject, false);
          break;
      }
    } else {
      this.navigate($event.item.cells.Title.value);
    }
  }

  navigate(card: any) {
    const result = {
      action: 'Redirect',
      board: this.data.input.board,
      card: card
    };
    const dialogRef = this.data['dialogRef'];
    dialogRef.close(result);
    if (card.id && card.parentTicket && card.parentTicket.id) {
      this.taskManagerService.parentTaskId = card.parentTicket.id; // todo: remove. we dont need to open parent task explicitely
      this.sendViewSubtaskActivityCommand(card);
    }
  }

  sendViewSubtaskActivityCommand(card) {
    const viewSubTaskFromParentTaskActivityCommand = new PlatformCommand('ViewSubTaskFromParentTaskActivityCommand', this.data.parameters.taskApi);
    viewSubTaskFromParentTaskActivityCommand.fieldValues['TaskId'] = card.id;
    viewSubTaskFromParentTaskActivityCommand.fieldValues['ParentTaskId'] = card.parentTicket.id;
    this.commandService.executePlatformCommand(viewSubTaskFromParentTaskActivityCommand).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
        console.log(response);
      },
      error => {
        console.log(error);
      });
  }

  onSelectionChanged($event) {
  }

  // addSubTask () {
  //   this.taskManagerService.addTask(null, this.card.board, null, null, this.card.id, this.invalidateSubject);
  // }

  updateTask(card: any, isTaskDateChange = false) {
    this.syncTaskStatusFromBaseTask(card);
    const isNewTask = !card.id;
    const isSubTask = !!card.parentTicket;
    const updateTaskCommand = this.taskManagerService.getAddOrUpdateTaskCommand(this.board, card, isTaskDateChange, card.parentTicket?.id);
    //updateTaskCommand.isCamelCase = true;
    this.commandService.executePlatformCommand(updateTaskCommand).pipe(take(1)).subscribe(response => {
      //console.log('update: ', response);
        const ticket = response.fieldValues['Ticket'];
        if (isNewTask) {
          this.currentlyExpandedSubtaskId = ticket.Id; // want to expend newly added sub task
          this.pageNo = 0; // pagination reset
        }
        // invalidate subtask
        if (isNewTask || !isSubTask) { // for newly added subtask or any update in parent task need to invalidate sub task
          this.getSubtasks(this.parentCard.id, this.card.scope, this.pageNo, this.pageSize);
        }
        this.invalidateTaskView(isNewTask ? this.parentCard : (this.card.id === card.id || !isSubTask) ? this.card : null, isNewTask);
    }, err => {
      let errorMessage = 'Update failed!';
      if (isNewTask) {
        errorMessage = 'Add task failed!';
      } else if (err && err.Payload) {
        errorMessage = err.Payload;
      }
      this.snackBarService.showError(errorMessage);
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private openEditor(event) {
    const minWidth = '800px';
    const maxWidth = '800px';

    this.dialogService.showCellEditorDialog(event.item, event.fieldDefinition, minWidth, maxWidth, this.sourceKey).subscribe(state => {
      if (state.action === 'OK') {
        // this.saveFieldDefinition(state.data.input.fieldDefinition, event.fieldSection);

        const item = {id: event.item.id, fieldValues: {}};
        const value = event.item.cells[event.fieldDefinition.key].value;

        const command = new PlatformCommand('EditTaskCommand', 'TaskManagerApi');
        command.fieldValues[event.fieldDefinition.key] = value;
        command.fieldValues['Id'] = event.item.id;
        this.commandService.executePlatformCommand(command).pipe().subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  handleActionEvent($event: MouseEvent, type: string, item: any) {
    $event.stopPropagation();
    switch (type) {
      case 'TaskAssignee':
      case 'TaskAlert':
      case 'defaultBodyView':
        this.actionEvent$.emit({id: type, type: 'OpenTask', card: item});
        break;
    }
  }

  onExpandSubtask(item: any) {
    setTimeout(() => {
      this.currentlyExpandedSubtaskId = item.id;
    }, 50);
  }

  onClosesSubtask(item: any) {
    this.currentlyExpandedSubtaskId = '';
  }

  subtaskPagination(pageNo) {
    this.currentlyExpandedSubtaskId = '';
    this.getSubtasks(this.parentCard.id, this.parentCard.scope, pageNo, this.pageSize, '');
  }

  createSubTask($event: any) {
    const card = {title: $event.text, status: 'Pending', parentTicket: {id: this.parentCard.id}};
    this.updateTask(card);
  }


  getParentTicket(id, scope) {
    this.fuseSplashScreenService.show();
    this.graphQLService.executePostRequest(this.api, TaskManagerQuery.getSelectedTask(id, scope)).pipe(take(1))
      .subscribe(payload => {
        this.fuseSplashScreenService.hide();
        if (payload && payload.data && payload.data.ticketWithBoard) {
          this.parentCard = payload.data.ticketWithBoard;
          this.setCardPermission(this.parentCard);
        } else {
          this.snackBarService.showError('Task not found!');
        }
      });
  }

  private navigateToSelectiveSubTaskView(taskSubTaskViewId: string) {
    const subTaskView = document.getElementById(taskSubTaskViewId);
    if (subTaskView) {
      subTaskView.scrollIntoView(false);
    }
  }

  onTaskStatusChanges(card, $event: any) {
    card.status = $event;
    const isSubTask = !!card.parentTicket;
    const updateTaskCommand = new UpdateStatusCommand(card.id, card.status);
    updateTaskCommand.scope = card.scopeInt;
    this.commandService.executeCommand(updateTaskCommand).pipe(take(1)).subscribe(response => {
      //this.invalidateTaskViews();
      if (!isSubTask) { // for newly added subtask or any update in parent task need to invalidate sub task
        this.getSubtasks(this.parentCard.id, this.card.scope, this.pageNo, this.pageSize);
      }
      this.invalidateTaskView((this.card.id === card.id || !isSubTask) ? this.card : null);
      this.syncTaskStatusWithBaseTask(card);
    });
    // this.updateTask(card);
  }

  private subscribeToInvalidatorListener() {
    this.taskManagerStore.pipe(select(getTaskViewUpdateInfo)).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(updatedTaskViewInfo => {
      if (updatedTaskViewInfo && updatedTaskViewInfo.card && updatedTaskViewInfo.card.id === this.card.id) {
        this.invalidateCard(updatedTaskViewInfo.card, updatedTaskViewInfo.sourceViewId);
      }
    });
  }

  private invalidateCard(updatedCard, sourceViewId: string) {
   if (sourceViewId === this.id) {
     this.syncTaskStatusWithBaseTask(updatedCard); // we are doing this as no hard and for date change it will bring latest status for a task
      return;
    }
    this.setCardPermission(updatedCard);
    if (updatedCard.id === this.card.id) {
      this.card = updatedCard;
    }
    if (updatedCard.id === this.parentCard.id) {
      this.parentCard = updatedCard;
    } else if (this.subTasks && this.subTasks.length > 0) {
      const index =  this.subTasks.findIndex(sub => sub.id === updatedCard.id);
      if (index !== -1) {
        this.subTasks[index] = updatedCard;
      }
    }
  }

  private invalidateTaskView(card: any, invalidateTaskToolbarNavigationSystem = false) {
    this.data.input.taskInvalidator.next({card: card, sourceViewId: this.id, invalidateTaskToolbarNavigationSystem: invalidateTaskToolbarNavigationSystem});
  }

  private setCardPermission(card: any) {
    card.hasPermissionForChangeStatus = card
      && (TicketOwner <= card.permissionLevel && card.permissionLevel <= TicketReviewer);
  }

  private setPermission() {
    if (this.card && this.card.fieldValues && this.card.fieldValues.length > 0) {
      const hasFlowIdKey = (ele) => ele.Key === 'FlowId';
      this.statusHidden = this.card.fieldValues.findIndex(hasFlowIdKey) > -1;
    }
  }

  private setSubTasksPermission() {
    if (this.subTasks && this.subTasks.length > 0) {
      this.subTasks.forEach(card => {
        this.setCardPermission(card);
      });
    }
  }

  handleActionEven($event: MouseEvent, defaultBodyView: string, parentCard: any) {

  }

  private syncTaskStatusWithBaseTask(card: any) {
    if (card && card.id === this.card.id) {
      this.card.status = card.status;
    }
    if (card && card.id === this.parentCard.id) {
      this.parentCard.status = card.status;
    }

    if (card && this.subTasks && this.subTasks.length > 0) {
      const index =  this.subTasks.findIndex(sub => sub.id === card.id);
      if (index !== -1) {
        this.subTasks[index].status = card.status;
      }
    }
  }

  private syncTaskStatusFromBaseTask(card: any) {
    if (card && card.id === this.card.id) {
      card.status = this.card.status;
      return;
    }
    if (card && card.id === this.parentCard.id) {
      card.status = this.parentCard.status;
      return;
    }

    if (this.subTasks && this.subTasks.length > 0) {
      const index =  this.subTasks.findIndex(sub => sub.id === card.id);
      if (index !== -1) {
       card.status =  this.subTasks[index].status;
      }
    }
  }
}
